import {
  CheckCircle2,
  Facebook,
  Instagram,
  Linkedin,
  Mail,
  MapPin,
  Phone,
  Twitter,
  Youtube,
} from "lucide-react"

export function Footer() {
  const companyName = process.env.NEXT_PUBLIC_COMPANY_NAME || "StockWise"
  const companyEmail = process.env.NEXT_PUBLIC_COMPANY_EMAIL || "barakhelmusic@gmail.com"
  const companyPhone = process.env.NEXT_PUBLIC_COMPANY_PHONE || "+226 61 74 22 74"
  const companyAddress =
    process.env.NEXT_PUBLIC_COMPANY_ADDRESS || "Samandin, Ouagadougou, Burkina Faso"

  const socialLinks = [
    {
      label: "Instagram",
      href: process.env.NEXT_PUBLIC_SOCIAL_INSTAGRAM || "#",
      icon: Instagram,
    },
    {
      label: "Facebook",
      href: process.env.NEXT_PUBLIC_SOCIAL_FACEBOOK || "#",
      icon: Facebook,
    },
    {
      label: "X",
      href: process.env.NEXT_PUBLIC_SOCIAL_X || "#",
      icon: Twitter,
    },
    {
      label: "YouTube",
      href: process.env.NEXT_PUBLIC_SOCIAL_YOUTUBE || "#",
      icon: Youtube,
    },
    {
      label: "LinkedIn",
      href: process.env.NEXT_PUBLIC_SOCIAL_LINKEDIN || "#",
      icon: Linkedin,
    },
  ]

  const isExternalUrl = (url: string) => /^https?:\/\//i.test(url)

  const services = [
    { label: "Boutique", href: "/boutique" },
    { label: "Commander", href: "/commande" },
    { label: "Recherche", href: "/recherche" },
  ]

  const support = [
    { label: "À propos", href: "/a-propos" },
    { label: "Nous contacter", href: "/contact" },
    { label: "Panier", href: "/panier" },
  ]

  return (
    <footer className="mt-12 bg-gradient-to-b from-primary-dark to-primary text-primary-foreground">
      <div className="container py-12">
        <div className="grid gap-10 md:grid-cols-12">
          {/* Marque + description + réseaux */}
          <div className="md:col-span-4">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-xl bg-primary-foreground/10 flex items-center justify-center">
                <img src="/logo_sw.png" alt="Logo" className="h-10 w-10 object-contain" />
              </div>
              <div>
                <p className="text-lg font-bold leading-none">{companyName}</p>
                <p className="mt-1 text-xs text-primary-foreground/70">
                  Vente de matériel de sonorisation
                </p>
              </div>
            </div>

            <p className="mt-4 max-w-sm text-sm text-primary-foreground/70">
              Équipez-vous avec des références fiables : instruments, sonorisation, home‑studio et accessoires.
            </p>

            <div className="mt-6 flex items-center gap-3">
              {socialLinks.map((item) => {
                const Icon = item.icon
                const external = isExternalUrl(item.href)
                return (
                  <a
                    key={item.label}
                    href={item.href}
                    target={external ? "_blank" : undefined}
                    rel={external ? "noreferrer" : undefined}
                    aria-label={item.label}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-primary-foreground/10 text-primary-foreground transition hover:bg-primary-foreground/20"
                  >
                    <Icon className="h-5 w-5" />
                  </a>
                )
              })}
            </div>
          </div>

          {/* Services */}
          <div className="md:col-span-3">
            <h3 className="text-base font-bold">Nos services</h3>
            <ul className="mt-4 space-y-3 text-sm text-primary-foreground/80">
              {services.map((item) => (
                <li key={item.href} className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-warning" />
                  <a href={item.href} className="transition hover:text-primary-foreground">
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Support */}
          <div className="md:col-span-2">
            <h3 className="text-base font-bold">Support</h3>
            <ul className="mt-4 space-y-3 text-sm text-primary-foreground/80">
              {support.map((item) => (
                <li key={item.href} className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-warning" />
                  <a href={item.href} className="transition hover:text-primary-foreground">
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="md:col-span-3">
            <h3 className="text-base font-bold">Contact</h3>
            <div className="mt-4 space-y-4 text-sm text-primary-foreground/80">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-full bg-primary-foreground/10">
                  <Phone className="h-4 w-4 text-warning" />
                </div>
                <div>
                  <p className="font-semibold text-primary-foreground">Téléphone</p>
                  <p className="text-primary-foreground/70">{companyPhone}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-full bg-primary-foreground/10">
                  <Mail className="h-4 w-4 text-warning" />
                </div>
                <div>
                  <p className="font-semibold text-primary-foreground">Email</p>
                  <p className="text-primary-foreground/70">{companyEmail}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-full bg-primary-foreground/10">
                  <MapPin className="h-4 w-4 text-warning" />
                </div>
                <div>
                  <p className="font-semibold text-primary-foreground">Adresse</p>
                  <p className="text-primary-foreground/70">{companyAddress}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-10 border-t border-primary-foreground/15 pt-6 text-center text-sm text-primary-foreground/70">
          <p>
            &copy; {new Date().getFullYear()}, {companyName}. Tous droits réservés.
          </p>
        </div>
      </div>
    </footer>
  )
}
